import {
  DEACTIVATE_USER,
  DEACTIVATE_USER_ACTION,
  DEACTIVATE_USER_CONTENT,
  DEACTIVATE_USER_LABEL,
  DEFAULT_USER_DATA
} from '@library/constants/user';
import { useCallback, useContext } from 'react';
import { ConfirmationModal } from '@library/components/modals/ConfirmationModal';
import { CreateUserModal } from '@library/components/modals/CreateUserModal/CreateUserModal';
import { Drawer } from '@library/components/navigation/Drawer';
import { EditUser } from '@bo-components/userRoles/editUser/EditUser';
import { EditUserDetails } from '@bo-components/userRoles/editUser/EditUserDetails';
import { EditUserHeader } from '@bo-components/userRoles/editUser/EditUserHeader';
import { EditUserPermissions } from '@bo-components/userRoles/editUser/EditUserPermissions';
import { Loading } from '@library/components/Loading';
import type { UpdateUserRequest } from '@library/types/user';
import { UserRolesContext } from '@bo-hooks/user/useAdminUsersContext';
import { usePermissions } from '@bo-hooks/permissions/usePermissions';

export const UserRolesModals = () => {
  const {
    isSelectedUserLoading,
    modals,
    onSelectedUserId,
    onUsersUpdate,
    selectedUserData: selectedUser,
    updateAdminUser
  } = useContext(UserRolesContext);

  const {
    createModalOpen,
    deactivateUserModalOpen,
    editUserDrawerOpen,
    onCreateModalOpen,
    onDeactivateUserModalOpen,
    onEditUserDrawerOpen,
    onViewPermissionsDrawerOpen,
    viewPermissionsDrawerOpen
  } = modals;

  const { permissionsData: permissions } = usePermissions();

  const toggleDrawer = useCallback(
    (isOpen = false) => {
      if (deactivateUserModalOpen) return; // Prevent toggling drawer if selected/deactivated modal is open

      onEditUserDrawerOpen(isOpen);

      if (!isOpen) {
        setTimeout(() => {
          onViewPermissionsDrawerOpen(false);
          onSelectedUserId('');
        }, 500); // Delay to allow drawer to close before resetting state
      }
    },
    [
      deactivateUserModalOpen,
      onEditUserDrawerOpen,
      onViewPermissionsDrawerOpen,
      onSelectedUserId
    ]
  );

  const handleUpdateUser = useCallback(
    async (userReq: UpdateUserRequest) => {
      if (selectedUser?.id) {
        await updateAdminUser({
          id: selectedUser.id,
          ...userReq
        }).then(() => {
          onUsersUpdate();
        });
      }
    },
    [onUsersUpdate, selectedUser, updateAdminUser]
  );

  const handleDeactivateUser = useCallback(async () => {
    await handleUpdateUser(DEACTIVATE_USER_ACTION);
    onDeactivateUserModalOpen(false);
  }, [handleUpdateUser, onDeactivateUserModalOpen]);

  return (
    <>
      <Drawer
        isOpen={editUserDrawerOpen}
        onCloseDrawer={() => toggleDrawer(false)}
        direction="right"
      >
        {!isSelectedUserLoading ? (
          <EditUser>
            <EditUserHeader
              user={selectedUser ?? DEFAULT_USER_DATA}
              onClose={() => toggleDrawer(false)}
              onDeactivateUser={() => onDeactivateUserModalOpen(true)}
            />
            <div className="h-[calc(100vh-64px)] overflow-scroll">
              {!viewPermissionsDrawerOpen ? (
                <EditUserDetails
                  user={selectedUser ?? DEFAULT_USER_DATA}
                  onClose={() => toggleDrawer(false)}
                  onUpdateUser={handleUpdateUser}
                  onViewPermissions={() => onViewPermissionsDrawerOpen(true)}
                />
              ) : (
                <EditUserPermissions
                  permissions={permissions}
                  userRoles={selectedUser?.roles ?? []}
                  onClose={() => onViewPermissionsDrawerOpen(false)}
                />
              )}
            </div>
          </EditUser>
        ) : (
          <div className="flex h-72 justify-center">
            <Loading loadingMessage="Loading User Details" />
          </div>
        )}
      </Drawer>

      <CreateUserModal
        isOpen={createModalOpen}
        onCancelModal={() => onCreateModalOpen(false)}
      />

      <ConfirmationModal
        isOpen={deactivateUserModalOpen}
        confirmationCopy={DEACTIVATE_USER}
        onCancelModal={() => onDeactivateUserModalOpen(false)}
        handleConfirm={handleDeactivateUser}
        label={DEACTIVATE_USER_LABEL}
      >
        {DEACTIVATE_USER_CONTENT}
      </ConfirmationModal>
    </>
  );
};
